import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, Alert, Form as BsForm, Spinner } from 'react-bootstrap';
import { unlockVaultDekFromMasterPassword } from '../../services/CredentialVaultService';
import { useCredentialVaultCrypto } from './VaultCryptoProvider';
import './credentialVault.css';

/** Pixels to translate .modal-dialog so its viewport-centered position matches .content-body's horizontal center (Modal portals to body; RB scrollbar padding overwrites symmetric padding). */
function readContentBodyCenterShiftPx() {
  if (typeof window === 'undefined') return 0;
  const el = document.querySelector('#main-wrapper .content-body');
  if (!el) return 0;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const midContent = r.left + r.width / 2;
  const midViewport = vw / 2;
  return midContent - midViewport;
}

/** Keep horizontal shift in sync with sidebar / layout (Modal portals to body). */
export function useCredentialVaultModalContentShift(show) {
  const [shiftPx, setShiftPx] = useState(0);

  const measure = useCallback(() => {
    if (!show) {
      setShiftPx(0);
      return;
    }
    setShiftPx(readContentBodyCenterShiftPx());
  }, [show]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!show) return undefined;
    let id2;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => measure());
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2 != null) cancelAnimationFrame(id2);
    };
  }, [show, measure]);

  useEffect(() => {
    if (!show) return undefined;

    const el = document.querySelector('#main-wrapper .content-body');
    let ro;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }

    window.addEventListener('resize', measure);
    const main = document.getElementById('main-wrapper');
    const mo =
      main && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => measure())
        : null;
    if (main && mo) mo.observe(main, { attributes: true, attributeFilter: ['class'] });

    const t1 = window.setTimeout(measure, 50);
    const t2 = window.setTimeout(measure, 320);

    return () => {
      window.removeEventListener('resize', measure);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      mo?.disconnect();
      ro?.disconnect();
    };
  }, [show, measure]);

  return { shiftPx, remeasure: measure };
}

/**
 * Prompt master password (corporate) or personal vault password before navigating to add/edit,
 * before saving (`purpose="save"`), before delete confirmation (`purpose="delete"`),
 * or before revealing/copying a stored password on the detail page (`purpose="reveal"`).
 * Calls `onContinue({ dekBytes })` after a successful unlock so callers can encrypt without waiting on React state.
 */
export default function CredentialVaultNavUnlockModal({
  show,
  side,
  onHide,
  onContinue,
  purpose = 'navigate',
}) {
  const isPersonal = side === 'personal';
  const {
    retainCorporateVaultDekAfterUnlock,
    vaultIdleTimeoutMinutes,
    unlockPersonalVaultSession,
  } = useCredentialVaultCrypto();

  const { shiftPx: contentShiftPx, remeasure: remeasureContentShift } = useCredentialVaultModalContentShift(show);

  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const passwordInputRef = useRef(null);
  /** Chrome aggressively saves “master password” fields; brief readonly blocks autofill/save hooks until user focuses. */
  const [corporateUnlockInputPrimed, setCorporateUnlockInputPrimed] = useState(false);

  useEffect(() => {
    if (!show) {
      setPassword('');
      setShowPw(false);
      setBusy(false);
      setError('');
      setCorporateUnlockInputPrimed(false);
    }
  }, [show]);

  const handleClose = () => {
    if (busy) return;
    setPassword('');
    setShowPw(false);
    setBusy(false);
    setError('');
    onHide();
  };

  const resetAfterSuccess = () => {
    setPassword('');
    setShowPw(false);
    setBusy(false);
    setError('');
  };

  const handleCorporateSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password.trim()) {
      setError('Master password is required');
      return;
    }
    setBusy(true);
    try {
      const dekBytes = await unlockVaultDekFromMasterPassword(password.trim());
      retainCorporateVaultDekAfterUnlock(dekBytes);
      resetAfterSuccess();
      onContinue?.({ dekBytes });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Incorrect master password');
      setBusy(false);
    }
  };

  const handlePersonalSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password.trim()) {
      setError('Personal vault password is required');
      return;
    }
    setBusy(true);
    try {
      const dekBytes = await unlockPersonalVaultSession(password.trim());
      resetAfterSuccess();
      onContinue?.({ dekBytes });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Incorrect personal vault password');
      setBusy(false);
    }
  };

  const idlePhrase =
    vaultIdleTimeoutMinutes === 1 ? '1 minute' : `${vaultIdleTimeoutMinutes} minutes`;

  const heading =
    purpose === 'delete'
      ? 'Confirm deletion'
      : purpose === 'reveal'
        ? 'Verify vault password'
        : isPersonal
          ? 'Unlock personal vault'
          : 'Unlock Credentials Vault';

  let purposeCopyBody;
  if (purpose === 'save') {
    purposeCopyBody = isPersonal ? (
      <>
        Enter your personal vault password to encrypt and save this credential.
        {' '}
        The personal vault locks automatically after {idlePhrase} of inactivity.
      </>
    ) : (
      <>
        Enter your corporate master password to encrypt and save this credential.
        {' '}
        The Credentials Vault locks automatically after {idlePhrase} of inactivity.
      </>
    );
  } else if (purpose === 'reveal') {
    purposeCopyBody = isPersonal ? (
      <>
        Enter your personal vault password to view or copy this stored password.
        {' '}
        The personal vault locks automatically after {idlePhrase} of inactivity.
      </>
    ) : (
      <>
        Enter your corporate master password to view or copy this stored password.
        {' '}
        The Credentials Vault locks automatically after {idlePhrase} of inactivity.
      </>
    );
  } else if (purpose === 'delete') {
    purposeCopyBody = isPersonal ? (
      <>
        Enter your personal vault password to verify you can delete this credential.
        {' '}
        The personal vault locks automatically after {idlePhrase} of inactivity.
      </>
    ) : (
      <>
        Enter your corporate master password to verify you can delete this credential.
        {' '}
        The Credentials Vault locks automatically after {idlePhrase} of inactivity.
      </>
    );
  } else {
    purposeCopyBody = isPersonal ? (
      <>
        Enter your personal vault password to open the form.
        {' '}
        The personal vault locks automatically after {idlePhrase} of inactivity.
      </>
    ) : (
      <>
        Enter your corporate master password to open the form.
        {' '}
        The Credentials Vault locks automatically after {idlePhrase} of inactivity.
      </>
    );
  }

  const modalShiftStyle = show ? { '--cv-shift-x': `${contentShiftPx}px` } : undefined;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={!busy}
      className="credential-vault-nav-unlock-modal-root"
      dialogClassName="credential-vault-nav-unlock-modal"
      contentClassName="credential-vault-nav-unlock-modal__content"
      style={modalShiftStyle}
      onEntered={() => {
        requestAnimationFrame(() => remeasureContentShift());
        window.setTimeout(() => {
          passwordInputRef.current?.focus();
        }, 0);
      }}
    >
      <Modal.Body className="credential-vault-nav-unlock-modal__body">
        <Alert variant="light" className="credential-vault-full-page-alert credential-vault-full-page-alert--in-modal mb-0">
          <Alert.Heading className="h5 mb-3">{heading}</Alert.Heading>
          <BsForm onSubmit={isPersonal ? handlePersonalSubmit : handleCorporateSubmit} autoComplete="off">
            <p className="small text-muted mb-3">{purposeCopyBody}</p>
            {error ? <div className="text-danger small mb-3">{error}</div> : null}
            <BsForm.Group className="mb-3">
              <BsForm.Label>{isPersonal ? 'Personal vault password' : 'Master password'}</BsForm.Label>
              <div className="position-relative password-input-wrapper">
                <BsForm.Control
                  ref={passwordInputRef}
                  autoFocus
                  type={showPw ? 'text' : 'password'}
                  autoComplete='off'
                  name={isPersonal ? 'credentialVaultPersonalUnlockNonce' : 'credentialVaultCorporateUnlockNonce'}
                  readOnly={!isPersonal && !corporateUnlockInputPrimed}
                  onFocus={() => {
                    if (!isPersonal) setCorporateUnlockInputPrimed(true);
                  }}
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  disabled={busy}
                  id={`credential-vault-nav-unlock-${isPersonal ? 'personal' : 'corporate'}`}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  disabled={busy}
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas ${showPw ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                </button>
              </div>
            </BsForm.Group>
            <div className="task-form-toolbar toolbar toolbar-bottom pt-2 credential-vault-nav-unlock-toolbar">
              <button
                type="button"
                className="btn btn-cancel task-form-toolbar-btn"
                disabled={busy}
                onClick={handleClose}
              >
                Cancel
              </button>
              <button type="submit" className="new-user-btn credential-vault-gate-primary-btn" disabled={busy}>
                {busy ? (
                  <Spinner animation="border" size="sm" variant="light" className="me-2" />
                ) : (
                  <i className="fas fa-unlock me-2" aria-hidden />
                )}
                Continue
              </button>
            </div>
          </BsForm>
        </Alert>
      </Modal.Body>
    </Modal>
  );
}
